import isMobile from 'ismobilejs';
import { OptionsDataManager } from "../app/managers/OptionsDataManager";
import { AppDebug } from "../app/debug/AppDebug";
import { AppSettings } from "../app/managers/AppSettings";
import { OptionsItemKeys } from "../app/managers/LayoutDataManager";
import { OptionsItemLiveData } from "../types/types";
import { GetOrientation, ScreenOrientationType } from "../utils/GetOrientation";
import { GetViewportSize } from "../utils/GetViewportSize";
import { RectSize } from "../utils/RectSize";
import debounce from 'debounce';


export enum ScreenManagerEvents {
    RESIZE = "resize",
    ORIENTATION_CHANGE = "orientation_change",
}

export type ResizeEventParams = {
    windowRect: Phaser.Geom.Rectangle,
}

export type OrientationEventParams = {
    orientation: ScreenOrientationType,
}

/**
 * Corresponds to 
 * - screen ScaleManager->zoom and to 
 * - canvas size
 */
export enum GraphicsQuality {
    UNSET = -1,
    HIGH = 0,
    MEDIUM = 1,
    LOW = 2,
}

export enum ScreenTransitionDirection {
    CENTER_TO_LEFT,
    CENTER_TO_RIGHT,
    LEFT_TO_CENTER,
    RIGHT_TO_CENTER,
}

export interface ScreenTransitionData {
    startX: number;
    endX: number;
}

/**
 * Resolves app scaling, orientation
 * - on catching RESIZE event, computes orientation
 * - on catching RESIZE event, dispatches RESIZE events to listeners
 * - global scale override?
 * 
 * NOTE: dont confuse with @see Phaser.Scale.ScreenManager
 */
export class ScreenManager {
    public static scene: Phaser.Scene;

    private static eventEmitter: Phaser.Events.EventEmitter;
    private static screenScaleToScreenScaleMult: number[] = [
        1,      // GraphicsQuality.HIGH
        0.75,   // GraphicsQuality.MEDIUM
        0.5,    // GraphicsQuality.LOW
    ];

    /**
     * Using different scaling variants affects texture size, 
     * so we need to scale the texture inversely to preserve in-game size
     */
    public static importedTextureScale: number = 1;

    /**
     * Scale which all in-game textures should multiply by
     * - inverse of 'imported texture scale' from scaling variants
     */
    public static svScaleFactor: number = 1;

    private static curWindowRect: RectSize;
    private static curOrientation: ScreenOrientationType;
    private static graphicsQuality: GraphicsQuality = GraphicsQuality.HIGH;
    private static screenScaleMult: number;
    /**
     * use aspect ratio when you need consistent/same view across diff. res. (screen with same visual on diff. resolution but same aspect ratio)
     */
    public static curAspectRatio: number;
    /**
     * Reference resolution of designs
     * - take AR from here, for scaling purposes (compared to live AR)
     *   - we'll use 50/50 proportional resize width/height
     */
    private static refRes: RectSize = new RectSize(1920, 1080);
    // assume reference design is Landscape design
    private static refARLandscape: number = this.refRes.width / this.refRes.height;
    private static refARPortrait: number = this.refRes.height / this.refRes.width;
    /**
     * Multiplier to apply to asset scale, which results from 
     * differences between reference design AR and resolution amd
     * physical/actual/available screen space AR & resolution
     * 
     * NOTE: dpr is NOT considered in arScale; 
     *       consider it when computing assetScale @see GetAssetScale
     */
    public static arScale: number = 1;
    private static screenTransitionData: ScreenTransitionData = { startX: 0, endX: 0 };


    /**
     * Sets canvas size and ScaleManager zoom.
     * Expects responsive design (all elements should rescale/resize appropriately on their own)
     * @param triggerResize 
     */
    public static SetScreenScale(graphicsQuality: GraphicsQuality, force: boolean = false, triggerResize: boolean = false): void {
        if (force || (graphicsQuality != this.graphicsQuality)) {
            this.graphicsQuality = graphicsQuality;
            this.screenScaleMult = this.screenScaleToScreenScaleMult[graphicsQuality];
            this.scene.scale.zoom = 1 / (this.screenScaleMult * window.devicePixelRatio);
            if (triggerResize) {
                this.OnResize();
            }
        }
    }


    public static GetEventEmitter(): Phaser.Events.EventEmitter {
        return this.eventEmitter;
    }

    public static GetWindowRect(): RectSize {
        return this.curWindowRect;
    }

    public static GetScreenTransitionPositions(direction: ScreenTransitionDirection): ScreenTransitionData {
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        const left: number = -windowRect.width - AppSettings.general.screenExcessHideOverflow;
        const right: number = windowRect.width + AppSettings.general.screenExcessHideOverflow;
        switch (direction) {
            case ScreenTransitionDirection.RIGHT_TO_CENTER:
                this.screenTransitionData.startX = right;
                this.screenTransitionData.endX = 0;
                break;
            case ScreenTransitionDirection.CENTER_TO_LEFT:
                this.screenTransitionData.startX = 0;
                this.screenTransitionData.endX = left;
                break;
            case ScreenTransitionDirection.LEFT_TO_CENTER:
                this.screenTransitionData.startX = left;
                this.screenTransitionData.endX = 0;
                break;
            case ScreenTransitionDirection.CENTER_TO_RIGHT:
                this.screenTransitionData.startX = 0;
                this.screenTransitionData.endX = right;
                break;
        }
        return this.screenTransitionData;
    }

    public static GetOrientation(): ScreenOrientationType {
        return this.curOrientation;
    }

    public static GetAspectRatio(): number {
        return this.curAspectRatio;
    }

    /**
     * From @see quality
     * @see screenScaleToScreenScaleMult
     */
    public static GetScreenScaleMult(): number {
        return this.screenScaleMult;
    }

    public static Init(scene: Phaser.Scene): void {
        this.scene = scene;

        this.eventEmitter = new Phaser.Events.EventEmitter();
        this.curWindowRect = new RectSize();

        // keep orientation undefined, allowing to dispatch ORIENTATION_CHANGE on first RESIZE
        const curViewportRect = GetViewportSize();
        this.curOrientation = GetOrientation(curViewportRect.width, curViewportRect.height);
    }

    public static Build(): void {
        // handle resize through window.resize, since while Phaser.Scale.Mode=Phaser.Scale.NONE, Phaser.Scale.Events.RESIZE does NOT TRIGGER.
        // window.onresize = () => this.OnResize();
        window.onresize = debounce.debounce(()=>this.OnResize(), 300);

        this.GetSetGraphicsQuality();
    }

    public static GetSetGraphicsQuality(): void
    {
        // PARSE/APPLY GraphicsQuality
        const quality = this.GetGraphicsQuality();

        // resize screen to viewport size
        // also triggers RESIZE, which updates loadingScreen view
        // console.log("SET SCALE VIA (1) debug if exists, if not: (2) 'default for Options', if not: (3) Default for device (Mobile gets 75% and old mobile gets 50%-first run)")
        ScreenManager.SetScreenScale(quality, true, true); // force? trigger resize?
    }

    private static GetGraphicsQuality(): GraphicsQuality {
        // console.log("GetGraphicsQuality");

        let quality: number = GraphicsQuality.UNSET;
        const debugQualityIndex: GraphicsQuality = AppDebug.GetQualityIndex();
        let applicationGraphicsQuality: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.graphicsQuality);
        // const applicationGraphicsQualityIndex: GraphicsQuality = applicationGraphicsQuality.value as number;
        // console.log(applicationGraphicsQualityIndex)
        const applicationGraphicsQualityIndex: GraphicsQuality = applicationGraphicsQuality ? applicationGraphicsQuality.value as number : GraphicsQuality.UNSET;

        // if 'lang' was supplied through get parameter
        // - lang from GET params has highest priority (even higher than cached language)
        if (debugQualityIndex != GraphicsQuality.UNSET) {
            quality = debugQualityIndex;

            if (applicationGraphicsQuality) {
                // use for this session only (not to localStorage)
                applicationGraphicsQuality.value = debugQualityIndex;
            }
        }
        else {
            // if got localstorage language
            if (applicationGraphicsQualityIndex != GraphicsQuality.UNSET) {
                // use it
                quality = applicationGraphicsQualityIndex;
            }
        }
        // if all else fails, set default
        if (quality === GraphicsQuality.UNSET) {
            const _isMobile: boolean = isMobile(window.navigator).any;
            if (_isMobile) {
                // Super small physical screen res
                if (Math.max(
                    window.screen.width * window.devicePixelRatio, 
                    window.screen.height * window.devicePixelRatio) <= 1024) {
                    quality = GraphicsQuality.LOW;
                }
                // Default mobile/tablet
                else {
                    quality = GraphicsQuality.MEDIUM;
                }
            }
            else {
                quality = GraphicsQuality.HIGH;
            }
            // console.log(GraphicsQuality[quality]);

            // console.log("qraphicsQuality Auto-detecting qraphicsQuality based on device type..", GraphicsQuality[quality]);
            // save it
            if (applicationGraphicsQuality) {
                applicationGraphicsQuality.value = quality;
                OptionsDataManager.SaveSharedData();
            }
        }

        return quality;
    }

    /**
     * Return final asset scale considering:
     * - baseScale
     * - 
     * @param baseScale additional scale to specific asset (or not)
     * @param bias how much of an effect asAssetScale has on returned scale value
     */
    public static GetAssetScale(baseScale: number = 1, bias: number = 1): number {
        const arScale = bias == 1 ?
            this.arScale :
            this.arScale + (1 - this.arScale) * (1 - bias);

        // console.log("> arScale=",arScale.toFixed(2),"[+baseScale]=",(baseScale * arScale * this.svScaleFactor).toFixed(2));
        return baseScale * arScale * window.devicePixelRatio * this.screenScaleMult * this.svScaleFactor;
    }

    private static GetDeviceTextureScale(): number {
        // this.pixelRatio
        // deviceWidth uses 'actual width' / pixelRatio = reports lower than actually is
        // this however can be used for feature detection, in this case for choosing texture scale
        // window.outerWidth and window.outerHeight
        // const higherDimension: number = Math.max(window.outerWidth, window.outerHeight);
        // SD <-> 640 <-> MD <-> 1024 <-> HD
        // iPhone12 = 390x844 independent pixels, right in the middle for 75% res scale tex
        // iPhone7 = 320x568 independent pixels, right under 640, for 50% res scale tex

        // anything except firefox
        // if (!FeatureDetection.IsFirefox()) {
        //     if (higherDimension <= 640) {
        //         return 0.5;
        //     }
        //     else 
        //     if (higherDimension <= 1024) {
        //         return 0.75;
        //     }
        //     return 1;
        // }
        // // firefox
        // else {
        //     if (higherDimension <= 768) {
        //         return 0.5;
        //     }
        //     return 1.0;
        // }
        return 1;
    }

    /**
     * manually call onresize
     */
    public static OnResizeManual(): void {
        this.OnResize();
    }

    private static OnResize(): void {
        const curViewportSize = GetViewportSize();

        const physSize = new RectSize(
            Math.ceil(curViewportSize.width * window.devicePixelRatio * this.screenScaleMult),
            Math.ceil(curViewportSize.height * window.devicePixelRatio * this.screenScaleMult),
        );

        const newOrientation: ScreenOrientationType = GetOrientation(curViewportSize.width, curViewportSize.height);

        let isOrientationChanged: boolean = false;
        if (this.curOrientation != newOrientation) {
            isOrientationChanged = true;
            this.curOrientation = newOrientation;
        }

        // compute arScale
        if (newOrientation == ScreenOrientationType.LANDSCAPE) {
            // const physARLandscape: number = physSize.width / physSize.height;
            const arLandscape: number = curViewportSize.width / curViewportSize.height;
            // console.log(physARLandscape.toFixed(2), this.refARLandscape.toFixed(2));
            if (arLandscape > this.refARLandscape) {
                // EDIT: stop considering dpr in arScale
                // this.arScale = physSize.height / this.refRes.height;
                this.arScale = curViewportSize.height / this.refRes.height;
            }
            else {
                // this.arScale = physSize.width / this.refRes.width;
                this.arScale = curViewportSize.width / this.refRes.width;
            }
        }
        else {
            // const physARPortrait: number = physSize.height / physSize.width;
            const arPortrait: number = curViewportSize.height / curViewportSize.width;
            // console.log(physARPortrait.toFixed(2), this.refARPortrait.toFixed(2));
            if (arPortrait > this.refARPortrait) {
                // this.arScale = physSize.width / this.refRes.width;
                this.arScale = curViewportSize.width / this.refRes.width;
            }
            else {
                // this.arScale = physSize.height / this.refRes.height;
                this.arScale = curViewportSize.height / this.refRes.height;
            }
        }
        // console.log(this.arScale.toFixed(2));

        this.curAspectRatio = curViewportSize.width / curViewportSize.height;

        // fix white lines/canvas not large enough to fill whole window
        // debug
        // this.curWindowRect.setSize(curViewportSize.width, curViewportSize.height);
        this.curWindowRect.Set(physSize.width, physSize.height);
        // console.log(this.curWindowRect.trace());

        // // manually resize canvas (since were using Phaser.Scale.Mode=Phaser.Scale.NONE)
        this.scene.scale.resize(this.curWindowRect.width, this.curWindowRect.height);

        // dispatch both in order
        // NOTE: at this point both orientation and size are cached!
        if (isOrientationChanged) {
            this.eventEmitter.emit(ScreenManagerEvents.ORIENTATION_CHANGE, this.curOrientation);
        }
        this.eventEmitter.emit(ScreenManagerEvents.RESIZE, this.curWindowRect);
    }
}
export class ResizeScene extends Phaser.Scene { }